import React from 'react';
import { X } from 'lucide-react';
import { EmbeddingIndexInfo } from '../types/embedding';

interface EmbeddingIndexSelectorProps {
  isOpen: boolean;
  embeddingIndexes: EmbeddingIndexInfo[];
  currentSelection?: string;
  onSelect: (indexName: string) => void;
  onClose: () => void;
}

export const EmbeddingIndexSelector: React.FC<EmbeddingIndexSelectorProps> = ({
  isOpen,
  embeddingIndexes,
  currentSelection,
  onSelect,
  onClose
}) => {
  if (!isOpen) return null;

  const handleRowClick = (indexName: string) => {
    onSelect(indexName);
  };

  const defaultIndex = embeddingIndexes.find(idx => idx.isDefault);

  return (
    <>
      <div data-flow="overlay" className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div data-flow="modal" className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">
              Select Embedding Index
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-auto flex-1">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Index Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-700 w-24">Default</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Default option row */}
                <tr
                  onClick={() => handleRowClick('')}
                  className={`cursor-pointer transition-colors ${
                    !currentSelection || currentSelection === ''
                      ? 'bg-blue-100 hover:bg-blue-200'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">Default</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    Use the system default embedding index{defaultIndex ? ` (${defaultIndex.indexName})` : ''}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                      System Default
                    </span>
                  </td>
                </tr>

                {/* Embedding index rows */}
                {embeddingIndexes.map((index) => (
                  <tr
                    key={index.indexName}
                    onClick={() => handleRowClick(index.indexName)}
                    className={`cursor-pointer transition-colors ${
                      currentSelection === index.indexName
                        ? 'bg-blue-100 hover:bg-blue-200'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{index.indexName}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{index.description}</td>
                    <td className="px-4 py-3 text-center">
                      {index.isDefault && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                          Default
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
